// HR admins choose what Ask HR reads: libraries and folders on Ask HR's SharePoint sites (the HR
// site, plus any IT has allowed), and websites. One line per source; clicking one opens its side
// panel (its documents, who it's for, who can open it, Remove). Add source opens a panel to browse a
// site for a library or folder, or add a website by address. The API re-checks everything: role,
// that the site is allowed, overlap, and that a website is public and reachable.

import { useEffect, useState } from "react";
import type { FolderListing, FolderRef, Library, Site } from "@ask-hr/core/graph";
import {
  APPLICABILITY_LABELS,
  ROOT,
  coveringSource,
  type ApplicabilityValue,
  type SharePointSource,
} from "@ask-hr/core/sources";
import { ApiError, api } from "../api.js";
import { ErrorNotice } from "../components/ErrorNotice.js";
import { Drawer } from "../components/Drawer.js";
import { Seg } from "../components/Seg.js";
import { linkTo, navigate } from "../router.js";
import type { Doc } from "./documents.js";
import { ApplicabilityOptions } from "./ApplicabilityOptions.js";
import { NeedsAttention } from "./NeedsAttention.js";
import { SortTh, sortRows, useSort } from "./sort.js";
import {
  SOURCE_DESC_FIRST,
  documentsLink,
  hostPath,
  label,
  sortSources,
  sourceCounts,
  type SourceCounts,
  type SourceSortKey,
} from "./sources.js";
import { SourceAccessDetail } from "./Visibility.js";


type Target = Pick<FolderRef, "driveId" | "driveName" | "itemId" | "name" | "path"> & { siteId?: string };
type SiteInfo = Site & { available: boolean };

export function SourcesPage() {
  const [sources, setSources] = useState<SharePointSource[] | null>(null);
  const [docs, setDocs] = useState<Doc[] | null>(null);
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [status, setStatus] = useState<string>("");
  const [applyTo, setApplyTo] = useState<ApplicabilityValue>("all");
  const [busy, setBusy] = useState<string | null>(null);
  // The source whose side panel is open, or the Add source panel (and which kind).
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState<"sharepoint" | "web" | null>(null);
  const [replacing, setReplacing] = useState<{ target: Target; sources: SharePointSource[] } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [sites, setSites] = useState<SiteInfo[]>([]);
  const [sort, sortBy] = useSort<SourceSortKey>(SOURCE_DESC_FIRST);

  const fetchSources = () => api<{ sources: SharePointSource[] }>("GET", "/api/admin/sources").then((r) => r.sources);
  // Counts only: the list still works without them.
  const fetchDocs = () =>
    api<{ documents: Doc[] }>("GET", "/api/admin/documents").then(
      (r) => r.documents,
      () => null,
    );

  const reload = async () => {
    try {
      const [s, d] = await Promise.all([fetchSources(), fetchDocs()]);
      setSources(s);
      setDocs(d);
      setError(null);
    } catch (e) {
      setError(e);
    }
  };

  useEffect(() => {
    let live = true;
    Promise.all([fetchSources(), fetchDocs()]).then(
      ([s, d]) => live && (setSources(s), setDocs(d)),
      (e: unknown) => live && setError(e),
    );
    // Errors show in the browser in Add source, which asks for the same sites' libraries.
    api<{ sites: SiteInfo[] }>("GET", "/api/admin/sharepoint/sites").then(
      (r) => live && setSites(r.sites),
      () => {},
    );
    api<{ last: { finishedAt: string } | null }>("GET", "/api/admin/sync").then(
      (r) => live && setLastSync(r.last?.finishedAt ?? null),
      () => {},
    );
    return () => {
      live = false;
    };
  }, []);

  // A source's site, named when it isn't the HR site (the first; sources from before there were
  // several sites have no siteId and are on it).
  const siteName = (s: SharePointSource) =>
    s.kind === "web" ? null : s.siteId && s.siteId !== sites[0]?.id ? (sites.find((x) => x.id === s.siteId)?.name ?? "Another site") : (sites[0]?.name ?? "HR site");
  // Where it is, under its name: the folder and site, or the web address.
  const whereOf = (s: SharePointSource) =>
    s.kind === "web" ? hostPath(s.webUrl) : `${s.path === "/" ? "Whole library" : s.path} · ${siteName(s)}`;

  async function add(target: Target, replace = false) {
    const key = `${target.driveId}~${target.itemId}`;
    setBusy(key);
    setStatus("");
    try {
      await api("POST", "/api/admin/sources", {
        siteId: target.siteId,
        driveId: target.driveId,
        itemId: target.itemId,
        applicability: applyTo,
        replace,
      });
      setReplacing(null);
      setStatus(`Added ${label(target)}. Its documents appear on Documents after the next sync.`);
      await reload();
    } catch (e) {
      if (e instanceof ApiError && e.body.error === "would_replace") {
        setReplacing({ target, sources: e.body.sources as SharePointSource[] });
      } else if (e instanceof ApiError && e.body.error === "already_included") {
        setStatus(`${label(target)} is already included.`);
        await reload();
      } else {
        setError(e);
      }
    } finally {
      setBusy(null);
    }
  }

  async function addWebsite(url: string): Promise<boolean> {
    setBusy("web");
    setStatus("");
    try {
      const r = await api<{ source: SharePointSource; found: number }>("POST", "/api/admin/sources", { url, applicability: applyTo });
      setStatus(
        r.source.feed === "lever"
          ? `Added ${r.source.driveName}: ${r.found} open ${r.found === 1 ? "job" : "jobs"}. They go live after the next sync, without review.`
          : `Added ${r.source.driveName}. Its text waits for review on the Documents tab after the next sync.`,
      );
      await reload();
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.body.error === "already_included") setStatus("That website is already on the list.");
      else setError(e);
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function setApplicability(s: SharePointSource, applicability: ApplicabilityValue) {
    setBusy(s.id);
    try {
      await api("PATCH", `/api/admin/sources/${encodeURIComponent(s.id)}`, { applicability });
      setStatus(`${label(s)} now applies to: ${APPLICABILITY_LABELS[applicability]}.`);
      await reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  async function remove(s: SharePointSource) {
    setBusy(s.id);
    try {
      await api("DELETE", `/api/admin/sources/${encodeURIComponent(s.id)}`);
      setConfirmRemove(null);
      setOpen(null);
      setStatus(`Removed ${label(s)}.`);
      await reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  const counts = docs ? sourceCounts(docs) : null;
  const all = sources ?? [];
  const web = all.filter((s) => s.kind === "web").length;
  const totalDocs = counts ? [...counts.values()].reduce((n, c) => n + c.total, 0) : null;
  const cantOpen = counts ? [...counts.values()].reduce((n, c) => n + c.hidden, 0) : 0;
  const panel = open ? all.find((s) => s.id === open) : undefined;
  const closeAdd = () => {
    setAdding(null);
    setReplacing(null);
  };

  return (
    <>
      <NeedsAttention only="sources" />
      <section className="panel" aria-labelledby="sources-h">
        <div className="dochead">
          <h2 id="sources-h">Sources</h2>
          {sources ? (
            <p className="facts">
              <span>
                <strong>{all.length}</strong> {all.length === 1 ? "source" : "sources"}
              </span>
              {all.length ? (
                <span>
                  {all.length - web} SharePoint · {web} {web === 1 ? "website" : "websites"}
                </span>
              ) : null}
              {totalDocs !== null ? (
                <span>
                  <strong>{totalDocs}</strong> documents
                </span>
              ) : null}
              {cantOpen ? (
                <a href="/admin/documents?tab=all&permissions=hidden" onClick={linkTo("/admin/documents?tab=all&permissions=hidden")}>
                  {cantOpen} documents employees can't open
                </a>
              ) : null}
              {lastSync ? <span>Synced {when(lastSync)}</span> : null}
            </p>
          ) : null}
          <button type="button" className="primary push" onClick={() => setAdding("sharepoint")}>
            Add source
          </button>
        </div>
        <p className="sub">
          Ask HR reads only what's listed here. Folders inside a source are included. New and changed documents wait for
          review on Documents, except Lever job postings, which go live on each hourly sync.
        </p>

        {error && !adding ? <ErrorNotice error={error} explain={explainSourcesError} /> : null}
        <p className="status" role="status">
          {adding ? "" : status}
        </p>

        {sources === null ? (
          error ? null : <p className="sub">Loading…</p>
        ) : sources.length === 0 ? (
          <p className="empty">No sources yet. Choose Add source to pick a SharePoint folder or a website.</p>
        ) : (
          <div className="tablewrap">
            <table className="docs">
              <thead>
                <tr>
                  <SortTh k="source" sort={sort} onSort={sortBy}>
                    Source
                  </SortTh>
                  <SortTh k="applies" sort={sort} onSort={sortBy}>
                    Applies to
                  </SortTh>
                  <th scope="col">Documents</th>
                  <SortTh k="added" sort={sort} onSort={sortBy}>
                    Added
                  </SortTh>
                  <th scope="col" className="go">
                    <span className="sr-only">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortSources(sources, sort).map((s) => {
                  const c = counts?.get(s.id);
                  return (
                    <tr key={s.id} className={`row${open === s.id ? " active" : ""}`} onClick={() => setOpen(s.id)}>
                      <td>
                        <div className="docname">
                          <button type="button" className="namebtn" onClick={() => setOpen(s.id)}>
                            {s.driveName}
                          </button>
                          {s.kind === "web" ? <span className="flag quiet">{s.feed === "lever" ? "Jobs · no review" : "Website"}</span> : null}
                          {c?.hidden ? (
                            <span className="flag lock" title={`Most employees can't open ${c.hidden} of its documents`}>
                              🔒 {c.hidden} can't open
                            </span>
                          ) : null}
                        </div>
                        <div className="muted small">{whereOf(s)}</div>
                      </td>
                      <td>
                        <span className={s.applicability === "all" ? "muted" : undefined}>{APPLICABILITY_LABELS[s.applicability]}</span>
                      </td>
                      <td className="nowrap" onClick={(e) => e.stopPropagation()}>
                        {c ? (
                          <>
                            <a href={documentsLink(s.id)} onClick={linkTo(documentsLink(s.id))} title="Open on Documents">
                              {c.total}
                            </a>
                            {c.review ? <span className="muted"> · {c.review} to review</span> : null}
                          </>
                        ) : (
                          <span className="muted">{docs ? "None yet" : ""}</span>
                        )}
                      </td>
                      <td className="muted nowrap" title={s.addedBy}>
                        {day(s.addedAt)}
                      </td>
                      <td className="go" aria-hidden="true">
                        ›
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {panel ? (
        <Drawer
          key={panel.id}
          title={panel.driveName}
          subtitle={
            <>
              {whereOf(panel)} ·{" "}
              <a href={panel.webUrl} target="_blank" rel="noreferrer">
                {panel.kind === "web" ? "Open website" : "Open in SharePoint"}
              </a>
            </>
          }
          onClose={() => {
            setOpen(null);
            setConfirmRemove(null);
          }}
        >
          <SourcePanel
            s={panel}
            counts={counts?.get(panel.id)}
            site={siteName(panel)}
            busy={busy === panel.id}
            confirming={confirmRemove === panel.id}
            onApplicability={(a) => void setApplicability(panel, a)}
            onConfirm={(on) => setConfirmRemove(on ? panel.id : null)}
            onRemove={() => void remove(panel)}
          />
        </Drawer>
      ) : null}

      {adding ? (
        <Drawer title="Add source" subtitle="A SharePoint library or folder, or a public website." onClose={closeAdd}>
          <section>
            <div className="toolbar">
              <Seg
                label="Kind of source"
                value={adding}
                onChange={(k) => {
                  setAdding(k);
                  setReplacing(null);
                }}
                options={[
                  ["sharepoint", "SharePoint folder"],
                  ["web", "Website"],
                ]}
              />
            </div>
            <label className="field">
              <span>New source applies to</span>
              <select value={applyTo} onChange={(e) => setApplyTo(e.target.value as ApplicabilityValue)}>
                <ApplicabilityOptions />
              </select>
            </label>
            {error ? <ErrorNotice error={error} explain={explainSourcesError} /> : null}
            <p className="status" role="status">
              {status}
            </p>
            {replacing ? (
              <div className="notice" role="alert">
                <p>
                  <strong>{label(replacing.target)}</strong> contains {replacing.sources.length === 1 ? "a source" : "sources"} already on
                  the list. Adding it replaces{" "}
                  {replacing.sources.map((s, i) => (
                    <span key={s.id}>
                      {i ? ", " : ""}
                      {label(s)} ({APPLICABILITY_LABELS[s.applicability]})
                    </span>
                  ))}
                  , and everything in it will apply to: {APPLICABILITY_LABELS[applyTo]}.
                </p>
                <div className="actions start">
                  <button type="button" className="primary" disabled={busy !== null} onClick={() => void add(replacing.target, true)}>
                    Replace and add
                  </button>
                  <button type="button" onClick={() => setReplacing(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}
            {adding === "web" ? (
              <WebsiteForm busy={busy !== null} onAdd={addWebsite} />
            ) : (
              <Browser sites={sites} sources={sources ?? []} busy={busy} onAdd={(t) => void add(t)} />
            )}
          </section>
        </Drawer>
      ) : null}
    </>
  );
}

// One source's side panel: its documents, who it's for, who can open it, details, and Remove.
function SourcePanel({
  s,
  counts: c,
  site,
  busy,
  confirming,
  onApplicability,
  onConfirm,
  onRemove,
}: {
  s: SharePointSource;
  counts: SourceCounts | undefined;
  site: string | null;
  busy: boolean;
  confirming: boolean;
  onApplicability: (a: ApplicabilityValue) => void;
  onConfirm: (on: boolean) => void;
  onRemove: () => void;
}) {
  const go = (to: string) => () => navigate(to);
  return (
    <>
      <section>
        <h4>Documents</h4>
        <div className="countbtns">
          <button type="button" onClick={go(documentsLink(s.id))}>
            <strong>{c?.total ?? 0}</strong>
            <span>documents</span>
          </button>
          <button type="button" disabled={!c?.review} onClick={go(documentsLink(s.id, { tab: "review" }))}>
            <strong>{c?.review ?? 0}</strong>
            <span>to review</span>
          </button>
          <button type="button" disabled={!c?.hidden} onClick={go(documentsLink(s.id, { permissions: "hidden" }))}>
            <strong>{c?.hidden ?? 0}</strong>
            <span>employees can't open</span>
          </button>
        </div>
        <p className="small muted">Each opens Documents filtered to this source.</p>
      </section>

      <section>
        <h4>Who it's for</h4>
        <label className="field">
          <span>Applies to</span>
          <select value={s.applicability} disabled={busy} onChange={(e) => onApplicability(e.target.value as ApplicabilityValue)}>
            <ApplicabilityOptions />
          </select>
        </label>
        <p className="hint">
          Its documents follow this unless one has its own setting
          {c?.own ? ` (${c.own} here ${c.own === 1 ? "does" : "do"})` : " (none here do)"}. Publish to make a change live.
        </p>
      </section>

      <section>
        <h4>Who can open it</h4>
        {s.kind === "web" ? (
          <p className="small">A public website: anyone can open it.</p>
        ) : s.visibility ? (
          <SourceAccessDetail v={s.visibility} />
        ) : (
          <p className="sub">Not checked yet. The next sync checks it.</p>
        )}
      </section>

      <section>
        <h4>Details</h4>
        <div className="field">
          <span>Kind</span>
          <span>
            {s.kind === "web"
              ? s.feed === "lever"
                ? "Lever jobs feed: each open job is a document, live without review"
                : "Website: one page, read as one document"
              : `SharePoint ${s.path === "/" ? "library" : "folder"} on ${site ?? "the HR site"}`}
          </span>
        </div>
        <div className="field">
          <span>Added</span>
          <span>
            {when(s.addedAt)} by {s.addedBy}
          </span>
        </div>
      </section>

      <section>
        <h4>Remove</h4>
        {confirming ? (
          <div className="actions start">
            <button type="button" className="danger" disabled={busy} onClick={onRemove}>
              Remove {label(s)}
            </button>
            <button type="button" onClick={() => onConfirm(false)}>
              Keep
            </button>
          </div>
        ) : (
          <div className="actions start">
            <button type="button" onClick={() => onConfirm(true)}>
              Remove…
            </button>
            <span className="small muted">Ask HR stops reading it. Its documents leave answers when you next publish.</span>
          </div>
        )}
      </section>
    </>
  );
}

const when = (iso: string) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
const day = (iso: string) => new Date(iso).toLocaleDateString();

type Crumb = { driveId: string; driveName: string; itemId: string; name: string };

// Browse a site for a library or folder to add (inside the Add source panel).
function Browser({
  sites,
  sources,
  busy,
  onAdd,
}: {
  sites: SiteInfo[];
  sources: SharePointSource[];
  busy: string | null;
  onAdd: (t: Target) => void;
}) {
  const [chosen, setChosen] = useState<string | null>(null);
  const [trail, setTrail] = useState<Crumb[]>([]);
  const [libraries, setLibraries] = useState<{ siteId: string; list: Library[] } | null>(null);
  const [listing, setListing] = useState<FolderListing | null>(null);
  const [error, setError] = useState<unknown>(null);
  // One sort for both lists (libraries, then folders), kept while moving between folders.
  const [sort, sortBy] = useSort<"name" | "items">(["items"]);
  const here = trail.at(-1);
  // The site being browsed: the one picked, else the HR site. Empty until the sites load: the API
  // then uses the HR site.
  const siteId = chosen ?? sites[0]?.id ?? "";
  const site = sites.find((s) => s.id === siteId);
  const siteQuery = siteId ? `siteId=${encodeURIComponent(siteId)}&` : "";

  useEffect(() => {
    let live = true;
    const load = here
      ? api<FolderListing>(
          "GET",
          `/api/admin/sharepoint/folder?${siteQuery}driveId=${encodeURIComponent(here.driveId)}&itemId=${encodeURIComponent(here.itemId)}`,
        ).then((l) => live && (setListing(l), setError(null)))
      : api<{ libraries: Library[] }>("GET", `/api/admin/sharepoint/libraries${siteQuery ? `?${siteQuery.slice(0, -1)}` : ""}`).then(
          (r) => live && (setLibraries({ siteId, list: r.libraries }), setError(null)),
        );
    load.catch((e: unknown) => live && setError(e));
    return () => {
      live = false;
    };
  }, [here, siteId, siteQuery]);

  const libs = libraries && libraries.siteId === siteId ? libraries.list : null;
  const shown = here && listing?.folder.driveId === here.driveId && listing.folder.itemId === here.itemId ? listing : null;
  const loading = here ? !shown : !libs;
  const several = sites.length > 1;
  const siteName = site?.name ?? "HR site";

  const pickSite = (id: string) => {
    setChosen(id);
    setTrail([]);
    setError(null);
  };

  const status = (driveId: string, path: string, target: Target) => {
    const by = coveringSource(sources, driveId, path);
    if (by) {
      return (
        <span className="pill" title={by.path === path ? "On the list" : `Included through ${label(by)}`}>
          Included
        </span>
      );
    }
    const key = `${target.driveId}~${target.itemId}`;
    return (
      <button
        type="button"
        disabled={busy !== null}
        aria-label={`Add ${label(target)}`}
        onClick={() => onAdd({ ...target, ...(siteId ? { siteId } : {}) })}
      >
        {busy === key ? "Adding…" : "Add"}
      </button>
    );
  };

  return (
    <div className="browser">
      {several ? (
        <label className="field">
          <span>Site</span>
          <select value={siteId} onChange={(e) => pickSite(e.target.value)}>
            {sites.map((s) => (
              <option key={s.id} value={s.id} disabled={!s.available}>
                {s.name}
                {s.available ? "" : " (not shared with Ask HR yet)"}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <nav className="crumbs" aria-label="Folder path">
        <button type="button" aria-current={trail.length === 0 ? "page" : undefined} onClick={() => setTrail([])}>
          {several ? siteName : "HR site"}
        </button>
        {trail.map((c, i) => (
          <span key={`${c.driveId}~${c.itemId}`}>
            <span aria-hidden="true">›</span>{" "}
            <button
              type="button"
              aria-current={i === trail.length - 1 ? "page" : undefined}
              onClick={() => setTrail(trail.slice(0, i + 1))}
            >
              {c.name}
            </button>
          </span>
        ))}
      </nav>

      {error ? <ErrorNotice error={error} explain={explainSourcesError} /> : null}

      {loading && !error ? <p className="sub">Loading…</p> : null}

      {!here && libs && !error ? (
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <SortTh k="name" sort={sort} onSort={sortBy}>
                  Library
                </SortTh>
                <th scope="col">
                  <span className="sr-only">Status</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {libs.length === 0 ? (
                <tr>
                  <td colSpan={2} className="muted">
                    Ask HR can't see any libraries on {several ? `the ${siteName} site` : "the HR site"}.
                  </td>
                </tr>
              ) : (
                sortRows(libs, sort?.key === "name" ? { key: "name", dir: sort.dir } : null, { name: (l) => l.name }, (l) => l.id).map((l) => (
                  <tr key={l.id}>
                    <td>
                      <button
                        type="button"
                        className="linkbtn"
                        onClick={() => setTrail([{ driveId: l.id, driveName: l.name, itemId: ROOT, name: l.name }])}
                      >
                        {l.name}
                      </button>
                    </td>
                    <td className="right">
                      {status(l.id, "/", { driveId: l.id, driveName: l.name, itemId: ROOT, name: l.name, path: "/" })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {here && shown && !error ? (
        <>
          <div className="toolbar">
            <span>
              <strong>{shown.folder.itemId === ROOT ? `${shown.folder.name} (whole library)` : shown.folder.name}</strong>
              <span className="muted">
                {" "}
                · {shown.files.supported} readable {shown.files.supported === 1 ? "file" : "files"} (PDF, Word)
                {shown.files.other ? `, ${shown.files.other} other` : ""} here
              </span>
            </span>
            {status(shown.folder.driveId, shown.folder.path, shown.folder)}
            <a href={shown.folder.webUrl} target="_blank" rel="noreferrer">
              Open in SharePoint
            </a>
          </div>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <SortTh k="name" sort={sort} onSort={sortBy}>
                    Folder
                  </SortTh>
                  <SortTh k="items" sort={sort} onSort={sortBy}>
                    Items
                  </SortTh>
                  <th scope="col">
                    <span className="sr-only">Status</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.folders.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="muted">
                      No folders inside.
                    </td>
                  </tr>
                ) : (
                  sortRows(shown.folders, sort, { name: (f) => f.name, items: (f) => f.childCount }, (f) => f.name).map((f) => (
                    <tr key={f.itemId}>
                      <td>
                        <button
                          type="button"
                          className="linkbtn"
                          onClick={() =>
                            setTrail([...trail, { driveId: f.driveId, driveName: f.driveName, itemId: f.itemId, name: f.name }])
                          }
                        >
                          {f.name}
                        </button>
                      </td>
                      <td className="muted">{f.childCount}</td>
                      <td className="right">{status(f.driveId, f.path, f)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}

// Add a website by address (inside the Add source panel).
function WebsiteForm({ busy, onAdd }: { busy: boolean; onAdd: (url: string) => Promise<boolean> }) {
  const [url, setUrl] = useState("");
  return (
    <div>
      <p className="sub">
        A public https page, read as one document (links on it aren't followed), or a Lever jobs page such as
        https://jobs.lever.co/novara, where each open job becomes its own document. Pages that need sign-in or only
        draw their text with JavaScript can't be read.
      </p>
      <form
        className="toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          void onAdd(url.trim()).then((ok) => ok && setUrl(""));
        }}
      >
        <label className="grow">
          <span className="sr-only">Website address</span>
          <input type="url" required placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)} />
        </label>
        <button type="submit" className="primary" disabled={busy || !url.trim()}>
          {busy ? "Checking…" : "Add website"}
        </button>
      </form>
    </div>
  );
}

// What an API error means on this page; undefined for the general line (components/ErrorNotice).
function explainSourcesError(e: ApiError): string | undefined {
  if (e.body.error === "sharepoint_not_configured") return "The SharePoint connection isn't set up yet. IT needs to add the Ask HR backend's credentials.";
  if (e.body.error === "not_on_hr_site") return "That folder isn't on that SharePoint site any more. It may have been moved or deleted.";
  if (e.status === 403) return "Only HR admins (the HR.Admin role) can manage sources.";
  if (e.body.error === "web_unreachable") {
    return typeof e.body.status === "number" && e.body.status
      ? `That website answered with an error (HTTP ${e.body.status}). Check the address.`
      : e.body.code === "web_type"
        ? "That address isn't a web page or a Lever jobs feed Ask HR can read."
        : e.body.code === "web_blocked"
          ? "That address redirects somewhere Ask HR won't go (an internal or non-https address)."
          : "Ask HR couldn't reach that website. Check the address and try again.";
  }
  if (e.status === 400 && typeof e.body.detail === "string") return e.body.detail;
  if (e.body.error === "sharepoint_error") return "SharePoint returned an error. Try again in a minute.";
  return undefined;
}
